/**
 * Renders a single leaf nav row (no children). Handles the four badge
 * variants: alerts count, system-alerts count, ETM action count, and the
 * lone passport/staff red dots.
 */

import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Lock } from "lucide-react";

function NavLeafItemImpl({
  item,
  collapsed,
  mobileOpen,
  itemLocked,
  alertCount,
  systemAlertCount,
  unreadChatCount,
  etmActionCount,
  userRole,
  hasExpiredPassports,
  hasExpiredStaffDocs,
  onNav,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const Icon = item.icon;
  const active = location.pathname === item.path;
  const systemBadgeCount = item.isSystemAlerts ? systemAlertCount : 0;
  const canSeeETM = ["ops_manager", "mechanic", "super_admin", "pilot"].includes(userRole);
  const etmBadgeCount = item.path === "/maintenance" && canSeeETM ? etmActionCount : 0;
  // Alertas + Mensajes viven juntos: el badge del item de Alertas suma
  // notificaciones sin leer y conversaciones con chat sin leer.
  const alertsBadgeCount = item.isAlerts ? alertCount + unreadChatCount : 0;
  const showBadge = alertsBadgeCount > 0 || systemBadgeCount > 0 || etmBadgeCount > 0;
  const badgeCount =
    systemBadgeCount > 0 ? systemBadgeCount : etmBadgeCount > 0 ? etmBadgeCount : alertsBadgeCount;
  // Bug 16 audit: "Alertas 7" en sidebar no coincidía con "6 conflictos" en
  // OCC; son cosas distintas (notif+chat vs conflicts FRE). Tooltip explícito
  // al hover desambigua sin tener que abrir el panel.
  const badgeTitle = item.isAlerts
    ? t("nav.alertsBadgeTooltip", {
        alerts: alertCount,
        chats: unreadChatCount,
        defaultValue: `${alertCount} notificaciones + ${unreadChatCount} chats sin leer`,
      })
    : undefined;
  const showPassportDot = item.path === "/owners" && hasExpiredPassports;
  const showStaffDot = item.path === "/staff" && hasExpiredStaffDocs;
  const showCalendarChatDot = false;

  return (
    <button
      data-testid={`nav-${item.path.replace("/", "")}`}
      onClick={() => {
        if (!itemLocked) onNav(item.path);
      }}
      title={itemLocked ? t("offline.requiresConnection") : undefined}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200
        ${
          itemLocked
            ? "text-slate-600 cursor-not-allowed opacity-60"
            : active
              ? "bg-altiv-navy/20 text-white border border-altiv-navy/40"
              : "text-slate-400 hover:text-white hover:bg-slate-800/50"
        }
      `}
    >
      {/* Icon with dot badge when collapsed */}
      <div className="relative flex-shrink-0">
        <Icon className="w-[18px] h-[18px]" />
        {(showBadge || showCalendarChatDot) && collapsed && !mobileOpen && !itemLocked && (
          <span className="absolute -top-1 -right-1 w-[7px] h-[7px] bg-red-500 rounded-full" />
        )}
      </div>
      {(!collapsed || mobileOpen) && (
        <>
          <span className="flex-1 text-left">{t(item.labelKey)}</span>
          {itemLocked && <Lock className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />}
          {!itemLocked && showBadge && (
            <span
              title={badgeTitle}
              className="min-w-[20px] h-5 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5"
            >
              {badgeCount > 99 ? "99+" : badgeCount}
            </span>
          )}
          {!itemLocked && showCalendarChatDot && !showBadge && (
            <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
          )}
          {!itemLocked && showPassportDot && !showBadge && (
            <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
          )}
          {!itemLocked && showStaffDot && !showBadge && (
            <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
          )}
        </>
      )}
    </button>
  );
}

const NavLeafItem = memo(NavLeafItemImpl);
export default NavLeafItem;
